import React, { useContext, useEffect, useMemo, useState } from "react";
import { Table } from "react-bootstrap";
import Swal from "sweetalert2";
import { ThreeDots } from "react-loader-spinner";
import { DarkModeContext } from "../../context/DarkModeContext";
import "./ContenidoOperacionesAliado.css";
import { getToken } from "../../utils/auth";


const apiConsultar = process.env.REACT_APP_API_NARANJA_CONSULTAR;
const apiMeses = process.env.REACT_APP_API_NARANJA_MESES;
const apiExcelDia = process.env.REACT_APP_API_NARANJA_EXCEL_DIA;
const apiExcelMes = process.env.REACT_APP_API_NARANJA_EXCEL_MES;

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
];

const formatoMoneda = new Intl.NumberFormat("es-AR", {
  style: "currency",
  currency: "ARS",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const PagosNaranjaAliado = () => {
  const { darkMode } = useContext(DarkModeContext);
  const [meses, setMeses] = useState([]);
  const [mesSeleccionado, setMesSeleccionado] = useState("");
  const [data, setData] = useState([]);
  const [loadingMeses, setLoadingMeses] = useState(false);
  const [loadingTabla, setLoadingTabla] = useState(false);
  const [descargandoDia, setDescargandoDia] = useState(false);
  const [descargandoMes, setDescargandoMes] = useState(false);
  const [error, setError] = useState("");

  const resumen = useMemo(() => {
    return data.reduce(
      (acc, row) => {
        acc.totalBruto += row.TotalBruto ?? row.totalBruto ?? 0;
        acc.totalNeto += row.TotalNeto ?? row.totalNeto ?? 0;
        acc.cantOp += row.CantidadOperaciones ?? row.cantidadOperaciones ?? 0;
        acc.cantPag += row.CantidadPagadas ?? row.cantidadPagadas ?? 0;
        acc.cantPen += row.CantidadPendientes ?? row.cantidadPendientes ?? 0;
        return acc;
      },
      { totalBruto: 0, totalNeto: 0, cantOp: 0, cantPag: 0, cantPen: 0 }
    );
  }, [data]);

  useEffect(() => {
    cargarMeses();
  }, []);

  const consultar = async (anio, mes) => {
    const token = getToken();
    if (!token) return;

    const hoy = new Date();
    const anioFinal = anio ?? hoy.getFullYear();
    const mesFinal = mes ?? hoy.getMonth() + 1;

    setLoadingTabla(true);
    setError("");
    try {
      const response = await fetch(apiConsultar, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ Token: token, Anio: anioFinal, Mes: mesFinal }),
      });

      if (!response.ok) throw new Error("No pudimos consultar los pagos Naranja.");
      const json = await response.json();
      setData(Array.isArray(json) ? json : []);
    } catch (err) {
      setError(err.message || "No pudimos consultar los pagos Naranja.");
      setData([]);
    } finally {
      setLoadingTabla(false);
    }
  };

  const cargarMeses = async () => {
    const token = getToken();
    if (!token) return;

    setLoadingMeses(true);
    setError("");
    try {
      const response = await fetch(apiMeses, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ Token: token }),
      });

      if (!response.ok) throw new Error("No pudimos cargar los meses disponibles.");
      const json = await response.json();

      const mapped = (json || []).map((m) => ({
        anio: m.Anio ?? m.anio,
        mes: m.Mes ?? m.mes,
        value: `${m.Anio ?? m.anio}-${String(m.Mes ?? m.mes).padStart(2, "0")}`,
        label: `${MESES[(m.Mes ?? m.mes) - 1]} ${m.Anio ?? m.anio}`,
      }));

      const hoy = new Date();
      const anioActual = hoy.getFullYear();
      const mesActual = hoy.getMonth() + 1;
      const valueActual = `${anioActual}-${String(mesActual).padStart(2, "0")}`;
      const existeActual = mapped.find((m) => m.value === valueActual);
      const listaFinal = existeActual
        ? mapped
        : [{ anio: anioActual, mes: mesActual, value: valueActual, label: `${MESES[mesActual - 1]} ${anioActual}` }, ...mapped];

      setMeses(listaFinal);
      setMesSeleccionado(valueActual);
      await consultar(anioActual, mesActual);
    } catch (err) {
      setError(err.message || "No pudimos cargar los meses.");
      setMeses([]);
      setData([]);
    } finally {
      setLoadingMeses(false);
    }
  };

  const onBuscar = () => {
    if (!mesSeleccionado) return;
    const [anio, mes] = mesSeleccionado.split("-").map(Number);
    consultar(anio, mes);
  };

  const descargarDia = async (fechaFila) => {
    const token = getToken();
    if (!token) return;

    const fecha = formatearFechaApi(fechaFila);
    if (!fecha) return;

    setDescargandoDia(true);
    try {
      const response = await fetch(apiExcelDia, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ Token: token, Fecha: fecha }),
      });

      if (!response.ok) throw new Error("No pudimos descargar el dia.");
      await descargarBlob(response, `pagos_naranja_dia_${fecha.replaceAll("/", "")}.xlsx`);
    } catch (err) {
      Swal.fire({ title: "Error", text: err.message || "No pudimos descargar el dia.", icon: "warning", confirmButtonText: "OK" });
    } finally {
      setDescargandoDia(false);
    }
  };

  const descargarMes = async () => {
    const token = getToken();
    if (!token) return;

    const [anio, mes] = mesSeleccionado
      ? mesSeleccionado.split("-").map(Number)
      : [new Date().getFullYear(), new Date().getMonth() + 1];

    setDescargandoMes(true);
    try {
      const response = await fetch(apiExcelMes, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ Token: token, Anio: anio, Mes: mes }),
      });

      if (!response.ok) throw new Error("No pudimos descargar el mes.");
      await descargarBlob(response, `pagos_naranja_mes_${anio}_${String(mes).padStart(2, "0")}.xlsx`);
    } catch (err) {
      Swal.fire({ title: "Error", text: err.message || "No pudimos descargar el mes.", icon: "warning", confirmButtonText: "OK" });
    } finally {
      setDescargandoMes(false);
    }
  };

  return (
    <div className="operaciones-aliado-wrap">
      <div className={darkMode ? "operaciones-aliado-card operaciones-aliado-card-dark" : "operaciones-aliado-card"}>
        <div className="operaciones-aliado-section-header">
          <div>
            <h3 className="operaciones-aliado-section-title">Pagos Naranja</h3>
            <p className="operaciones-aliado-section-copy mb-0">
              Vista equivalente a excepcion anticipo para operaciones Naranja con fecha efectiva de cobro el dia 14.
            </p>
          </div>
        </div>

        <div className="operaciones-aliado-banner">
          <span className="operaciones-aliado-banner-icon">N</span>
          <div className="operaciones-aliado-banner-copy">
            <strong>Seguimiento puntual de Naranja</strong>
            <span>Consulta solo el flujo asociado a Naranja, agrupado por dia de cobro, con exportacion del dia o del mes completo.</span>
          </div>
        </div>

        <div className="operaciones-aliado-pill-row">
          <span className="operaciones-aliado-pill operaciones-aliado-pill-accent">Solo Naranja</span>
          <span className="operaciones-aliado-pill">Cobro dia 14</span>
          <span className="operaciones-aliado-pill">Exportacion XLSX</span>
        </div>

        <div className="operaciones-aliado-month-toolbar">
          <div className="operaciones-aliado-field operaciones-aliado-field-month">
            <label className="operaciones-aliado-label">Mes a consultar</label>
            <select
              className="operaciones-aliado-select"
              value={mesSeleccionado}
              onChange={(e) => setMesSeleccionado(e.target.value)}
              disabled={loadingMeses || meses.length === 0}
            >
              <option value="">Seleccionar mes</option>
              {meses.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>

          <div className="operaciones-aliado-actions operaciones-aliado-actions-end">
            <button className="operaciones-aliado-btn operaciones-aliado-btn-primary" onClick={onBuscar} disabled={!mesSeleccionado || loadingTabla}>
              {loadingTabla ? "Consultando..." : "Buscar"}
            </button>
            <button className="operaciones-aliado-btn operaciones-aliado-btn-dark" onClick={descargarMes} disabled={descargandoMes || loadingMeses}>
              {descargandoMes ? "Descargando..." : "Descargar mes"}
            </button>
          </div>
        </div>

        {data.length > 0 && (
          <div className="operaciones-aliado-kpi-grid">
            <div className="operaciones-aliado-kpi-card">
              <span className="operaciones-aliado-kpi-label">Bruto acumulado</span>
              <strong className="operaciones-aliado-kpi-value">{formatoMoneda.format(resumen.totalBruto)}</strong>
              <span className="operaciones-aliado-kpi-foot">Monto bruto de Naranja en el mes</span>
            </div>
            <div className="operaciones-aliado-kpi-card">
              <span className="operaciones-aliado-kpi-label">Neto acumulado</span>
              <strong className="operaciones-aliado-kpi-value">{formatoMoneda.format(resumen.totalNeto)}</strong>
              <span className="operaciones-aliado-kpi-foot">Monto neto disponible del periodo</span>
            </div>
            <div className="operaciones-aliado-kpi-card">
              <span className="operaciones-aliado-kpi-label">Operaciones</span>
              <strong className="operaciones-aliado-kpi-value">{resumen.cantOp}</strong>
              <span className="operaciones-aliado-kpi-foot">Movimientos identificados en Naranja</span>
            </div>
            <div className="operaciones-aliado-kpi-card">
              <span className="operaciones-aliado-kpi-label">Pendientes</span>
              <strong className="operaciones-aliado-kpi-value">{resumen.cantPen}</strong>
              <span className="operaciones-aliado-kpi-foot">Operaciones aun sin fecha pagada</span>
            </div>
          </div>
        )}

        <div className="operaciones-aliado-table-shell">
          {loadingTabla || loadingMeses ? (
            <div className="operaciones-aliado-loading-inline">
              <ThreeDots visible height="46" width="46" color="#b4c400" />
            </div>
          ) : error ? (
            <EstadoVacio titulo="No pudimos cargar la tabla" texto={error} />
          ) : !data || data.length === 0 ? (
            <EstadoVacio titulo="Sin pagos Naranja para este mes" texto="Cuando existan operaciones Naranja con cobro el dia 14 las veras resumidas aqui." />
          ) : (
            <>
              <div className="operaciones-aliado-table-toolbar">
                <span className="operaciones-aliado-table-note">Dias con movimientos: {data.length}</span>
                <span className="operaciones-aliado-inline-note">Solo se incluyen operaciones Naranja con fecha efectiva dia 14.</span>
              </div>
              <div className="table-responsive operaciones-aliado-table-responsive">
                <Table className="operaciones-aliado-table" hover>
                  <thead>
                    <tr className={darkMode ? "operaciones-aliado-head-row operations-aliado-head-row-dark" : "operaciones-aliado-head-row"}>
                      <th>Fecha</th>
                      <th>Total bruto</th>
                      <th>Total neto</th>
                      <th>Operaciones</th>
                      <th>Pagadas</th>
                      <th>Pendientes</th>
                      <th>Excel dia</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((row, idx) => {
                      const fecha = row.Fecha ?? row.fecha;
                      const totalBruto = row.TotalBruto ?? row.totalBruto ?? 0;
                      const totalNeto = row.TotalNeto ?? row.totalNeto ?? 0;
                      const cantOp = row.CantidadOperaciones ?? row.cantidadOperaciones ?? 0;
                      const cantPag = row.CantidadPagadas ?? row.cantidadPagadas ?? 0;
                      const cantPen = row.CantidadPendientes ?? row.cantidadPendientes ?? 0;

                      return (
                        <tr key={`${fecha || "row"}-${idx}`}>
                          <td>{formatearFecha(fecha)}</td>
                          <td>{formatoMoneda.format(totalBruto)}</td>
                          <td>{formatoMoneda.format(totalNeto)}</td>
                          <td>{cantOp}</td>
                          <td>{cantPag}</td>
                          <td>{cantPen}</td>
                          <td>
                            <button className="operaciones-aliado-btn operaciones-aliado-btn-dark operaciones-aliado-btn-sm" onClick={() => descargarDia(fecha)} disabled={descargandoDia}>
                              {descargandoDia ? "..." : "Descargar"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="operaciones-aliado-total-row">
                      <td>Total</td>
                      <td>{formatoMoneda.format(resumen.totalBruto)}</td>
                      <td>{formatoMoneda.format(resumen.totalNeto)}</td>
                      <td>{resumen.cantOp}</td>
                      <td>{resumen.cantPag}</td>
                      <td>{resumen.cantPen}</td>
                      <td>-</td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const EstadoVacio = ({ titulo, texto }) => (
  <div className="operaciones-aliado-empty-box">
    <div className="operaciones-aliado-empty-icon">i</div>
    <strong className="operaciones-aliado-empty-title">{titulo}</strong>
    <span className="operaciones-aliado-empty-copy">{texto}</span>
  </div>
);

const formatearFecha = (value) => {
  if (!value) return "-";
  const fecha = new Date(value);
  if (Number.isNaN(fecha.getTime())) return "-";
  return fecha.toLocaleDateString("es-AR");
};

const formatearFechaApi = (value) => {
  const fecha = new Date(value);
  if (Number.isNaN(fecha.getTime())) return null;
  const dd = String(fecha.getDate()).padStart(2, "0");
  const mm = String(fecha.getMonth() + 1).padStart(2, "0");
  const yyyy = fecha.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const descargarBlob = async (response, fileName) => {
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export default PagosNaranjaAliado;
